<template>
	<div class="container py-5">
		<ToastNotification
			v-model:show="toastShow"
			:title="toastTitle"
			:message="toastMessage"
		/>
		<div class="row justify-content-center">
			<div class="col-lg-8">
				<div class="card shadow">
					<div class="card-header bg-white">
						<div class="d-flex justify-content-between align-items-center">
							<h3 class="mb-0">Add New User</h3>
						</div>
					</div>
					<div class="card-body">
						<div v-if="error" class="alert alert-danger">{{ error }}</div>
						<form
							class="needs-validation"
							novalidate
							@submit.prevent="handleSubmit"
						>
							<div class="mb-3">
								<label for="displayName" class="form-label required-field">
									Display Name
								</label>
								<input
									id="displayName"
									v-model="form.displayName"
									class="form-control"
									required
									type="text"
								/>
							</div>

							<div class="mb-3">
								<label for="username" class="form-label required-field">
									Username
								</label>
								<input
									id="username"
									v-model="form.username"
									class="form-control"
									required
									type="text"
								/>
								<div class="form-text">
									Must contain only letters, numbers, and underscores.
								</div>
							</div>

							<div class="mb-3">
								<label for="email" class="form-label"> Email Address </label>
								<input
									id="email"
									v-model="form.email"
									class="form-control"
									type="email"
								/>
							</div>

							<div class="mb-3">
								<label for="certificationNumber" class="form-label">
									Certification Number
								</label>
								<input
									id="certificationNumber"
									v-model="form.certificationNumber"
									class="form-control"
									type="text"
								/>
								<div class="form-text">
									Optional. The applicator's certificate or licence number.
								</div>
							</div>

							<div class="mb-3">
								<label for="certificationFile" class="form-label">
									Certification File
								</label>
								<input
									id="certificationFile"
									ref="certificationFileInput"
									type="file"
									class="form-control"
									accept="application/pdf,image/*"
									@change="handleCertificationChange"
								/>
								<div class="form-text">
									Optional. A PDF or an image (a scan or photo) of the
									certificate.
								</div>
							</div>

							<div class="row">
								<div class="col-md-6 mb-3">
									<label for="role" class="form-label required-field">
										Role
									</label>
									<select
										id="role"
										v-model="form.role"
										class="form-select"
										required
									>
										<option value="" disabled>Select a role</option>
										<option
											v-for="option in roleOptions"
											:key="option.value"
											:value="option.value"
										>
											{{ option.label }}
										</option>
									</select>
								</div>

								<div v-if="isRoot" class="col-md-6 mb-3">
									<label for="organization" class="form-label required-field">
										Organization
									</label>
									<select
										id="organization"
										v-model="form.organizationID"
										class="form-select"
										required
									>
										<option value="" disabled>Select an organization</option>
										<option
											v-for="org in organizations"
											:key="org.id"
											:value="org.id"
										>
											{{ org.name }}
										</option>
									</select>
								</div>
							</div>

							<hr />

							<div class="d-flex justify-content-end gap-2">
								<button
									class="btn btn-secondary"
									type="button"
									@click="$router.push('/_/configuration/user')"
								>
									Cancel
								</button>
								<button
									class="btn btn-primary"
									:disabled="isLoading"
									type="submit"
								>
									<i class="bi bi-person-plus me-1"></i>
									{{ isLoading ? "Adding..." : "Add User" }}
								</button>
							</div>
						</form>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { apiClient } from "@/client";
import { useSessionStore } from "@/store/session";
import { useUserStore } from "@/store/user";
import ToastNotification from "@/components/ToastNotification.vue";

const router = useRouter();
const session = useSessionStore();
const userStore = useUserStore();

const form = ref({
	displayName: "",
	username: "",
	email: "",
	certificationNumber: "",
	role: "account-owner",
	organizationID: null as number | null,
});

const isLoading = ref(false);
const error = ref("");
const organizations = ref<{ id: number; name: string }[]>([]);
const certificationFileInput = ref<HTMLInputElement | null>(null);
const selectedCertificationFile = ref<File | null>(null);
const toastShow = ref(false);
const toastTitle = ref("");
const toastMessage = ref("");

function showToast(title: string, message: string) {
	toastTitle.value = title;
	toastMessage.value = message;
	toastShow.value = true;
}

function handleCertificationChange(event: Event) {
	const target = event.target as HTMLInputElement;
	selectedCertificationFile.value = target.files?.[0] ?? null;
}

const isRoot = computed(() => session.self?.role === "root");

const roleOptions = computed(() => {
	const options = [
		{ value: "account-owner", label: "Account Owner" },
		{ value: "vision-reviewer", label: "Vision Reviewer" },
	];
	if (isRoot.value) {
		options.unshift({ value: "root", label: "Root" });
		options.splice(1, 0, { value: "staff", label: "Staff" });
	}
	return options;
});

onMounted(async () => {
	if (isRoot.value) {
		try {
			const orgs = await apiClient.JSONGet<{ id: number; name: string }[]>(
				"/api/sudo/organization",
			);
			organizations.value = orgs;
		} catch (e: any) {
			error.value = "Failed to load organizations.";
		}
	}
});

async function handleSubmit() {
	error.value = "";
	if (!form.value.displayName.trim() || !form.value.username.trim()) {
		error.value = "Display name and username are required.";
		return;
	}
	if (!form.value.role) {
		error.value = "Please select a role.";
		return;
	}
	if (isRoot.value && !form.value.organizationID) {
		error.value = "Please select an organization.";
		return;
	}

	isLoading.value = true;
	try {
		let certificationURI: string | undefined;
		if (selectedCertificationFile.value) {
			try {
				const formData = new FormData();
				formData.append("file", selectedCertificationFile.value);
				const url = session.urls?.api.certification;
				if (!url) {
					throw new Error("certification upload url is not configured");
				}
				const response = await fetch(url, {
					body: formData,
					method: "POST",
				});
				if (!response.ok) {
					throw new Error(`Upload failed: ${response.statusText}`);
				}
				const data = await response.json();
				certificationURI = data.uri;
			} catch (uploadError: any) {
				console.error("Failed to upload certification", uploadError);
				showToast(
					"Certification Upload Failed",
					uploadError?.message ?? "Failed to upload certification file.",
				);
				isLoading.value = false;
				return;
			}
		}

		const payload: Record<string, unknown> = {
			display_name: form.value.displayName.trim(),
			username: form.value.username.trim(),
			role: form.value.role,
		};
		if (form.value.email.trim()) {
			payload.email = form.value.email.trim();
		}
		if (isRoot.value && form.value.organizationID) {
			payload.organization_id = form.value.organizationID;
		}
		if (certificationURI) {
			payload.certification = certificationURI;
		}
		const certificationNumber = form.value.certificationNumber.trim();
		if (certificationNumber) {
			payload.certification_number = certificationNumber;
		}
		await apiClient.JSONPost("/api/user", payload);
		await userStore.fetchAll(isRoot.value);
		router.push("/_/configuration/user");
	} catch (e: any) {
		const msg =
			e?.response?.data?.message || e?.message || "Failed to create user.";
		error.value = msg;
	} finally {
		isLoading.value = false;
	}
}
</script>

<style scoped>
.required-field::after {
	content: " *";
	color: red;
}
</style>
